import classNames from 'classnames';
import * as React from 'react';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';

/**
 * The five kinds of message the app has. `neutral` is for context that is neither good nor bad —
 * an explanatory note on a form, say — and replaces the hand-written grey boxes.
 */
export type MessageBoxStatus = 'info' | 'warning' | 'error' | 'success' | 'neutral';

/**
 * Material symbol shown per status. Overridable with the `icon` prop, suppressible with `icon={null}`.
 *
 * The legacy `InformationBox` family rendered an ⓘ for every status, including errors. That is fixed
 * here — the status now picks its own icon.
 *
 * All five are circular so a column of boxes reads as one set. That is why `warning` is Material's
 * `error` (a circled exclamation) rather than `warning` (a triangle), and why `error` is `cancel` (a
 * circled cross): Material has exactly one circled exclamation, so the two statuses cannot both use
 * it. Trade-off worth knowing — the triangle is the stronger warning convention, and this trades a
 * little of that urgency for a consistent set.
 */
const STATUS_ICONS: Record<MessageBoxStatus, string> = {
	info: 'info',
	warning: 'error',
	error: 'cancel',
	success: 'check_circle',
	neutral: 'info',
};

/**
 * Tailwind classes per status, resolved from the `msg` colour scale in tailwind.config.js, which in
 * turn reads the `--fh-msg-*` custom properties in src/styles/tailwind.css. That indirection is what
 * lets the legacy SCSS box in information-box.scss paint identical colours off the same source.
 */
const STATUS_CLASSES: Record<MessageBoxStatus, string> = {
	info: 'bg-msg-info-bg border-msg-info-border text-msg-info-text',
	warning: 'bg-msg-warning-bg border-msg-warning-border text-msg-warning-text',
	error: 'bg-msg-error-bg border-msg-error-border text-msg-error-text',
	success: 'bg-msg-success-bg border-msg-success-border text-msg-success-text',
	neutral: 'bg-msg-neutral-bg border-msg-neutral-border text-msg-neutral-text',
};

const STATUS_ICON_CLASSES: Record<MessageBoxStatus, string> = {
	info: 'text-msg-info-icon',
	warning: 'text-msg-warning-icon',
	error: 'text-msg-error-icon',
	success: 'text-msg-success-icon',
	neutral: 'text-msg-neutral-icon',
};

export interface MessageBoxProps {
	/** Defaults to `info`, the most common case by call-site count. */
	status?: MessageBoxStatus;
	/** Bold first line. Omit for a single-sentence box. */
	title?: React.ReactNode;
	/** The message. Plain strings are wrapped in a `<p>`; nodes are rendered as given. */
	children?: React.ReactNode;
	/**
	 * Buttons, rendered in a row to the right on wide boxes and wrapped underneath when there is not
	 * room. Absorbs what `InformationBoxWithButton` and `InformationBoxWithButtons` used to do.
	 */
	actions?: React.ReactNode;
	/** Material symbol name. Defaults to the status icon; `null` renders no icon at all. */
	icon?: string | null;
	/** Tighter padding, for boxes sitting inside a form row or a card. */
	slim?: boolean;
	/**
	 * Drops the body from 16px to 14px, for a box sitting in dense chrome — a dashboard widget, a
	 * table toolbar, a filter bar — where full body size would dominate what it annotates.
	 */
	smallText?: boolean;
	/** Collapses the box, animating height and opacity, without unmounting it. */
	hidden?: boolean;
	/** Pulses a halo in the status colour three times — used to re-draw the eye to a failed save. */
	errorShake?: boolean;
	/** Removes the default bottom margin when the parent already spaces its children. */
	noBottomMargin?: boolean;
	onClick?: () => void;
	className?: string;
	/** Stable hook for e2e flows. */
	testId?: string;
}

/**
 * The app's one information / warning / error / success box.
 *
 * Replaces `InformationBox`, `InformationBoxWithButton`, `InformationBoxWithButtons` and `InfoBox`,
 * and the hand-written Tailwind boxes that grew because the old `InfoBox` had no warning or success
 * variant. Anything that needs a tinted, bordered message belongs here — see the Feedback &
 * Messaging section of the Component Library page for the full set.
 */
export function MessageBox({
	status = 'info',
	title,
	children,
	actions,
	icon,
	slim = false,
	smallText = false,
	hidden = false,
	errorShake = false,
	noBottomMargin = false,
	onClick,
	className,
	testId,
}: MessageBoxProps) {
	// `undefined` means "use the status icon"; `null` means "no icon". Both are falsy, so the two
	// cases have to be told apart explicitly.
	const iconName = icon === undefined ? STATUS_ICONS[status] : icon;

	return (
		<div
			className={classNames(
				// Stable BEM hooks. Tailwind styles the component; these exist so a page can adjust a
				// box's placement in its own stylesheet (margins, stacking, button width) without
				// reaching into utility classes. Sixteen pages already do.
				'message-box',
				`message-box--${status}`,
				slim && 'message-box--slim',
				!!actions && 'message-box--with-actions',
				hidden && 'hidden',
				// items-center so a button sits against the middle of a two-line message rather than
				// riding up level with the icon. The body below keeps items-start for the icon.
				'flex flex-row flex-wrap items-center justify-between gap-3 rounded-lg border',
				STATUS_CLASSES[status],
				slim ? 'p-2' : 'p-4',
				noBottomMargin ? 'mb-0' : 'mb-3',
				// 16px by default, matching the app's body text, so a box reads as part of the page
				// rather than as shrunken chrome. The icon follows in `em` (message-box.scss).
				smallText ? 'text-sm' : 'text-base',
				hidden && 'invisible m-0 max-h-0 overflow-hidden !border-0 !p-0 opacity-0',
				errorShake && 'animate-msg-flash',
				onClick && 'cursor-pointer',
				'transition-all duration-300 ease-in-out',
				className,
			)}
			// Re-exposes the current status's tokens under fixed names, so anything rendered inside the
			// box can take its colour from the box without knowing which status it is. Used by the
			// msg-flash keyframe and by the action-button styling in message-box.scss.
			style={
				{
					'--fh-msg-bg': `var(--fh-msg-${status}-bg)`,
					'--fh-msg-border': `var(--fh-msg-${status}-border)`,
					'--fh-msg-icon': `var(--fh-msg-${status}-icon)`,
					'--fh-msg-text': `var(--fh-msg-${status}-text)`,
					'--fh-msg-accent': `var(--fh-msg-${status}-border)`,
				} as React.CSSProperties
			}
			data-test-id={testId}
			onClick={onClick}
			onKeyDown={() => {}}
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
		>
			<div className="message-box__body flex min-w-[200px] flex-1 flex-row items-start gap-3">
				{iconName !== null && (
					<Icon
						useMaterialIcons
						name={iconName}
						// Size and line-height come from message-box.scss so the icon can scale with the
						// box's text size in `em`; a utility class here would be outranked anyway.
						classname={classNames('message-box__icon shrink-0', STATUS_ICON_CLASSES[status])}
					/>
				)}
				{/*
				 * Block, NOT `flex flex-col`. A body written as inline JSX —
				 * `Currently valid on <strong>all routes</strong> and <strong>all stops</strong>.` —
				 * reaches here as an array of text nodes and elements. As flex items each one became
				 * its own line; in normal flow they run together as a sentence, while block children
				 * (paragraphs, lists) still stack. `min-w-0` stays for truncation inside the flex row.
				 *
				 * `flex-1` so the column fills the width left by the icon. Without it the column is a
				 * flex item at its default `0 1 auto` — content width — and any child sizing itself
				 * against the column (`w-full`, or a `justify-between` row) collapses to the width of
				 * the text instead of spanning the box.
				 */}
				<div className="message-box__content min-w-0 flex-1">
					{/* Weight and spacing live in message-box.scss — see the note there on `.frontend p`. */}
					{!!title && <p className="message-box__title">{title}</p>}
					{typeof children === 'string' ? <p>{children}</p> : children}
				</div>
			</div>
			{!!actions && <div className="message-box__actions flex h-fit flex-row flex-wrap gap-2">{actions}</div>}
		</div>
	);
}

export default MessageBox;
